/**
 * Set 1 — interactive week intake chart (actual macros, tap a day for detail).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, Platform, Pressable, type TextStyle } from 'react-native';
import moment from 'moment';
import { useTranslation, formatNumber, getLocale } from '../../../../shared/i18n';
import { horizontalLtrDirectionStyle } from '../../../../shared/i18n/rtl';
import { useRtl } from '../../../../shared/i18n/useRtl';
import { useAuthNavigation } from '../../../../shared/navigation/useAuthNavigation';
import {
  type DayMacroSnapshot,
  useProgressiveMacroWeekData,
} from '../../../../data/domains/dashboard';
import { useCaloriesLimitVisible } from '../../../../data/domains/config';
import {
  resolveDashboardCalorieGoal,
  useSubscriptionContext,
} from '../../../../data/domains/subscription';
import type { ThemeColors } from '../../../../shared/theme';
import { appTheme } from '../../../../shared/theme';
import { hexToRgba } from '../../../../shared/theme/colorUtils';
import { DASH_STUDIO_INNER } from '../../../../shared/theme/studio/dashboardConstants';
import { formatTodayForApi } from '../../../../shared/utils/dateUtils';
import { triggerHaptic } from '../../../../shared/utils/haptics';
import { dashboardNestedTileShadow, dashboardSet1CardShell } from './dashboardSet1Styles';
import { DashboardSet1EmptyState } from './DashboardSet1EmptyState';

const ANDROID_TEXT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const BAR_MAX_H = 88;

export interface DashboardPlanWeekCardSet1Props {
  colors: ThemeColors;
  on_browse_plans?: () => void;
}

function weekdayLabel(date: string, locale: string): string {
  try {
    moment.locale(locale === 'ar' ? 'ar' : 'en');
  } catch {
    moment.locale('en');
  }
  return moment(date, 'YYYY-MM-DD', true).format('dd');
}

function dayHasMacros(day: DayMacroSnapshot): boolean {
  return (day.calories ?? 0) > 0 || (day.protein ?? 0) > 0 || (day.carbs ?? 0) > 0 || (day.fats ?? 0) > 0;
}

export function DashboardPlanWeekCardSet1({ colors, on_browse_plans }: DashboardPlanWeekCardSet1Props) {
  const { t } = useTranslation();
  const locale = getLocale();
  const rtl = useRtl();
  const navigation = useAuthNavigation();
  const writing_direction = rtl.writingDirection;
  const text_align = rtl.textAlign;
  const { activeSubscriptions, isLoading: subscription_loading } = useSubscriptionContext();
  const calories_limit_enabled = useCaloriesLimitVisible();
  const { data, dateFrom, dateTo, loaded } = useProgressiveMacroWeekData({ enable_fetch: false });
  const today = formatTodayForApi();
  const [selected_date, set_selected_date] = useState(today);

  useEffect(() => {
    if (data.some(d => d.date === selected_date)) {
      return;
    }
    const fallback = data.find(d => d.date === today) ?? data[data.length - 1];
    if (fallback) {
      set_selected_date(fallback.date);
    }
  }, [data, selected_date, today]);

  const selected = useMemo(
    () => data.find(d => d.date === selected_date) ?? data[data.length - 1] ?? null,
    [data, selected_date],
  );

  const cal_goal = resolveDashboardCalorieGoal({
    calories_limit_enabled,
    day_config: null,
    subscription: activeSubscriptions[0] ?? null,
  });

  const max_bar_cal = useMemo(() => {
    const day_max = Math.max(0, ...data.map(d => d.calories ?? 0));
    return Math.max(1, day_max, cal_goal > 0 ? cal_goal : 0);
  }, [data, cal_goal]);

  const week_stats = useMemo(() => {
    const active_days = data.filter(dayHasMacros);
    const total_cal = data.reduce((sum, d) => sum + (d.calories ?? 0), 0);
    const avg = active_days.length > 0 ? Math.round(total_cal / active_days.length) : 0;
    return { total_cal: Math.round(total_cal), avg, active_count: active_days.length };
  }, [data]);

  const range_label = useMemo(() => {
    try {
      moment.locale(locale === 'ar' ? 'ar' : 'en');
    } catch {
      moment.locale('en');
    }
    const from = moment(dateFrom, 'YYYY-MM-DD').format('MMM D');
    const to = moment(dateTo, 'YYYY-MM-DD').format('MMM D');
    return t('dashboard.planWeekRange').replace('{{from}}', from).replace('{{to}}', to);
  }, [dateFrom, dateTo, locale, t]);

  const selected_date_label = useMemo(() => {
    if (!selected) {
      return '';
    }
    try {
      moment.locale(locale === 'ar' ? 'ar' : 'en');
    } catch {
      moment.locale('en');
    }
    const m = moment(selected.date, 'YYYY-MM-DD', true);
    return `${m.format('ddd')} ${formatNumber(m.date())} ${m.format('MMM')}`;
  }, [selected, locale]);

  const on_select_day = useCallback((date: string) => {
    set_selected_date(date);
    triggerHaptic('selection');
  }, []);

  const track = hexToRgba(colors.text, 0.08) as string;
  const goal_line_top =
    cal_goal > 0 ? Math.max(0, BAR_MAX_H - Math.round((cal_goal / max_bar_cal) * BAR_MAX_H)) : null;
  const week_has_macros = data.some(dayHasMacros);
  const has_subscription = activeSubscriptions.length > 0;

  if (!subscription_loading && !has_subscription && !week_has_macros) {
    return (
      <DashboardSet1EmptyState
        colors={colors}
        icon="flame"
        title={t('dashboard.sectionPlanRhythm')}
        message={t('dashboard.planWeekNoSubscription')}
        cta_label={on_browse_plans ? t('dashboard.browsePlans') : undefined}
        on_cta={on_browse_plans}
      />
    );
  }

  if (loaded && has_subscription && !week_has_macros) {
    return (
      <DashboardSet1EmptyState
        colors={colors}
        icon="flame"
        title={t('dashboard.sectionPlanRhythm')}
        message={t('dashboard.selectMealsHint').replace('{{date}}', t('dashboard.today'))}
        cta_label={t('dashboard.exploreDishes')}
        on_cta={() => navigation.navigate('DailyMenuFlow')}
      />
    );
  }

  return (
    <View style={[dashboardSet1CardShell(colors), styles.card]}>
      <Text
        style={[styles.overline, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
        accessibilityRole="header">
        {t('dashboard.sectionPlanRhythm')}
      </Text>
      <Text style={[styles.range, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}>
        {range_label}
      </Text>

      <View style={[styles.detailTray, dashboardNestedTileShadow(colors), { backgroundColor: colors.surfaceElevated }]}>
        <Text style={[styles.detailDate, { color: colors.textSecondary, writingDirection: writing_direction }, ANDROID_TEXT]}>
          {selected_date_label}
        </Text>
        <View style={[styles.detailHero, horizontalLtrDirectionStyle]}>
          <Text style={[styles.detailKcal, { color: colors.text, writingDirection: writing_direction }, ANDROID_TEXT]}>
            {selected && (selected.calories ?? 0) > 0 ? formatNumber(Math.round(selected.calories)) : '—'}
          </Text>
          <Text style={[styles.detailUnit, { color: colors.textLight, writingDirection: writing_direction }, ANDROID_TEXT]}>
            {t('dashboard.kcal')}
          </Text>
        </View>
        <View style={[styles.macroChips, horizontalLtrDirectionStyle]}>
          <MacroChip
            label={t('dashboard.protein')}
            value={selected?.protein ?? 0}
            color={colors.protein}
            text_color={colors.text}
            writing_direction={writing_direction}
          />
          <MacroChip
            label={t('dashboard.carbs')}
            value={selected?.carbs ?? 0}
            color={colors.carbs}
            text_color={colors.text}
            writing_direction={writing_direction}
          />
          <MacroChip
            label={t('dashboard.fat')}
            value={selected?.fats ?? 0}
            color={colors.fats}
            text_color={colors.text}
            writing_direction={writing_direction}
          />
        </View>
      </View>

      <Text style={[styles.tapHint, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}>
        {t('dashboard.planWeekTapHint')}
      </Text>

      <View style={[styles.chartBlock, { backgroundColor: hexToRgba(colors.text, 0.03) as string }]}>
        {goal_line_top != null ? (
          <View
            pointerEvents="none"
            style={[
              styles.goalLine,
              {
                top: goal_line_top + appTheme.spacing.sm,
                borderColor: hexToRgba(colors.calorie || colors.primary, 0.45) as string,
              },
            ]}
          />
        ) : null}
        <View style={[styles.barRow, horizontalLtrDirectionStyle]}>
          {data.map(day => {
            const cal = Math.max(0, day.calories ?? 0);
            const bar_h = cal > 0 ? Math.max(8, Math.round((cal / max_bar_cal) * BAR_MAX_H)) : 4;
            const is_selected = day.date === selected_date;
            const is_today = day.date === today;
            const fill = is_selected
              ? colors.primary
              : cal > 0
                ? (hexToRgba(colors.primary, 0.45) as string)
                : track;
            return (
              <Pressable
                key={day.date}
                onPress={() => on_select_day(day.date)}
                accessibilityRole="button"
                accessibilityState={{ selected: is_selected }}
                accessibilityLabel={`${weekdayLabel(day.date, locale)}, ${formatNumber(Math.round(cal))} ${t('dashboard.kcal')}`}
                style={styles.barCol}>
                <View style={[styles.barTrack, { height: BAR_MAX_H }]}>
                  <View style={[styles.barFill, { height: bar_h, backgroundColor: fill }]} />
                </View>
                <Text
                  style={[
                    styles.dayLetter,
                    {
                      color: is_selected || is_today ? colors.primary : colors.textLight,
                      fontWeight: is_selected || is_today ? '800' : '600',
                    },
                    ANDROID_TEXT,
                  ]}>
                  {weekdayLabel(day.date, locale)}
                </Text>
                {is_selected ? (
                  <View style={[styles.selectedDot, { backgroundColor: colors.primary }]} />
                ) : (
                  <View style={styles.selectedDotSpacer} />
                )}
              </Pressable>
            );
          })}
        </View>
      </View>

      <View style={styles.statsRow}>
        <View style={[styles.statPill, { backgroundColor: colors.surfaceLight, borderColor: colors.borderLight }]}>
          <Text style={[styles.statValue, { color: colors.text }, ANDROID_TEXT]}>
            {week_stats.total_cal > 0 ? formatNumber(week_stats.total_cal) : '—'}
          </Text>
          <Text style={[styles.statLabel, { color: colors.textSecondary }, ANDROID_TEXT]} numberOfLines={2}>
            {t('dashboard.planWeekTotalKcal')}
          </Text>
        </View>
        <View style={[styles.statPill, { backgroundColor: hexToRgba(colors.primary, 0.1) as string, borderColor: colors.borderLight }]}>
          <Text style={[styles.statValue, { color: colors.primary }, ANDROID_TEXT]}>
            {week_stats.avg > 0 ? formatNumber(week_stats.avg) : '—'}
          </Text>
          <Text style={[styles.statLabel, { color: colors.textSecondary }, ANDROID_TEXT]} numberOfLines={2}>
            {t('dashboard.avgPerDay')}
          </Text>
        </View>
        {cal_goal > 0 ? (
          <View style={[styles.statPill, { backgroundColor: colors.surfaceLight, borderColor: colors.borderLight }]}>
            <Text style={[styles.statValue, { color: colors.text }, ANDROID_TEXT]}>{formatNumber(cal_goal)}</Text>
            <Text style={[styles.statLabel, { color: colors.textSecondary }, ANDROID_TEXT]} numberOfLines={2}>
              {t('dashboard.planWeekGoalKcal')}
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );
}

function MacroChip({
  label,
  value,
  color,
  text_color,
  writing_direction,
}: {
  label: string;
  value: number;
  color: string;
  text_color: string;
  writing_direction: 'ltr' | 'rtl';
}) {
  return (
    <View style={styles.macroChip}>
      <View style={[styles.macroDot, { backgroundColor: color }]} />
      <Text style={[styles.macroChipLabel, { color, writingDirection: writing_direction }, ANDROID_TEXT]} numberOfLines={1}>
        {label}
      </Text>
      <Text style={[styles.macroChipValue, { color: text_color, writingDirection: writing_direction }, ANDROID_TEXT]} numberOfLines={1}>
        {value > 0 ? `${formatNumber(Math.round(value))}g` : '—'}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: DASH_STUDIO_INNER,
  },
  overline: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.9,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  range: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: appTheme.spacing.md,
  },
  detailTray: {
    borderRadius: 14,
    padding: appTheme.spacing.md,
    marginBottom: appTheme.spacing.sm,
    gap: appTheme.spacing.xs,
  },
  detailDate: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  detailHero: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: appTheme.spacing.xs,
  },
  detailKcal: {
    fontSize: 32,
    fontWeight: '800',
    letterSpacing: -1,
    lineHeight: 34,
  },
  detailUnit: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 3,
  },
  macroChips: {
    flexDirection: 'row',
    gap: appTheme.spacing.sm,
    marginTop: appTheme.spacing.xs,
  },
  macroChip: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
  },
  macroDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  macroChipLabel: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  macroChipValue: {
    fontSize: 12,
    fontWeight: '800',
  },
  tapHint: {
    fontSize: 11,
    fontWeight: '500',
    marginBottom: appTheme.spacing.sm,
  },
  chartBlock: {
    borderRadius: 16,
    paddingTop: appTheme.spacing.sm,
    paddingHorizontal: appTheme.spacing.xs,
    paddingBottom: appTheme.spacing.sm,
    marginBottom: appTheme.spacing.md,
    overflow: 'hidden',
    position: 'relative',
  },
  goalLine: {
    position: 'absolute',
    left: appTheme.spacing.sm,
    right: appTheme.spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth * 2,
    borderStyle: 'dashed',
    zIndex: 1,
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: appTheme.spacing.xxs,
  },
  barCol: {
    flex: 1,
    alignItems: 'center',
    minWidth: 0,
    gap: 4,
  },
  barTrack: {
    width: '100%',
    maxWidth: 28,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  barFill: {
    width: '70%',
    minWidth: 10,
    maxWidth: 18,
    borderRadius: 8,
    minHeight: 4,
  },
  dayLetter: {
    fontSize: 10,
    letterSpacing: 0.2,
  },
  selectedDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
  },
  selectedDotSpacer: {
    width: 5,
    height: 5,
  },
  statsRow: {
    flexDirection: 'row',
    gap: appTheme.spacing.sm,
  },
  statPill: {
    flex: 1,
    minWidth: 0,
    borderRadius: 14,
    paddingVertical: appTheme.spacing.sm,
    paddingHorizontal: appTheme.spacing.sm,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    gap: 2,
  },
  statValue: {
    fontSize: 16,
    fontWeight: '800',
    letterSpacing: -0.3,
  },
  statLabel: {
    fontSize: 9,
    fontWeight: '600',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
});
